import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import ImageWithFallback from 'components/ImageWithFallback';
import FavouriteButton from 'components/prizes/FavouriteButton';
import UnfavouritePrizeModal from 'components/wish-list/UnfavouritePrizeModal';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { prizeApiClient } from 'utils/apiClient';
import { capitalizeFirstLetter, getSubscriberStatus } from 'utils/common';
import { ASSET_PREFIX, SubscriberStatus } from 'utils/constants';
import { PrizeBadgeOverlay } from './prize-page/PrizeBadgeOverlay';
import styles from './PrizeCard2026.module.css';
import { prizeBadgesForCards } from './PrizeCardWithTitleBelow';

type TPrize = {
  id: number;
  title: string;
  subheading: string;
  heroImageLocation: string;
  tierCode: string;
  drawTypePlans?: string[];
  isFromPlayersSuggestion?: boolean | null;
  position?: number; // in prize display group
  slug: string;
  badge?: string | null;
};

const updateFavourite = async (
  prizeId: number,
  newValue: boolean,
  refreshStore: () => Promise<void>,
) => {
  if (newValue) {
    await prizeApiClient.auth.favouritePrize({
      body: { prizeId },
    });
  } else {
    await prizeApiClient.auth.unfavouritePrize({
      body: { prizeId },
    });
  }
  await refreshStore();
};

interface IPrizeCard2026Props {
  prize: TPrize;
  showPrizeTierBadge?: boolean;
  //additionalComponentBadge: ReactNode | null;
  //onClick?: (event: React.MouseEvent<any>) => void;
  showLockedPrizeBannerIfApplicable?: boolean;
  /** The HTML element tag name for the prize. This should be the correct H1, H2, H3, etc for where the PrizeCard
   * appears in the rest of the page */
  prizeTitleElementTagName: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  imageAspectRatio: 'SQUARE' | 'WIDE';
}

const PrizeCard2026 = observer((props: IPrizeCard2026Props) => {
  const {
    currentUser,
    favouritedPrizeIds,
    fetchWishListPrizes,
    subscriptionStatusInfo,
  } = useUserStore();
  const router = useRouter();
  const subscriberStatus = getSubscriberStatus(subscriptionStatusInfo);
  const [showUnfavouritePrizeModal, setShowUnfavouritePrizeModal] =
    useState(false);
  const prizeId = props.prize.id;

  // Show locked prize banner if the prize is not winnable for the drawTypePlan user is subscribed to
  const showLockedPrizeBanner =
    subscriberStatus !== SubscriberStatus.NON_SUBSCRIBER &&
    props.prize.drawTypePlans &&
    props.prize.drawTypePlans.length > 0 &&
    !props.prize.drawTypePlans.includes(subscriberStatus);

  const handleFavouriteToggle = async () => {
    // If user is not logged in and they try to favourite a prize, redirect to /account/wish-list
    if (!currentUser) {
      await router.push('/account/wish-list');
      return;
    }

    if (favouritedPrizeIds.has(props.prize.id)) {
      // Unfavouriting, so show the confirmation modal.
      setShowUnfavouritePrizeModal(true);
    } else {
      // Favouriting the prize, so no modal, just do it.
      await updateFavourite(prizeId, true, fetchWishListPrizes);
    }
  };

  const additionalComponentBadge = props.prize.badge
    ? (prizeBadgesForCards[props.prize.badge] ??
      console.warn(
        `PrizeCard2026 attempted to render a ${props.prize.badge} which is not supported yet`,
      ) ??
      undefined)
    : null;

  const HeadingElement = props.prizeTitleElementTagName;
  const isFavourited = favouritedPrizeIds.has(props.prize.id);

  return (
    <figure className={styles.container}>
      <AspectRatioContainer
        width={props.imageAspectRatio === 'SQUARE' ? 1 : 319}
        height={props.imageAspectRatio === 'SQUARE' ? 1 : 221}
        innerClassName={styles.imageContainer}
      >
        <ImageWithFallback
          src={props.prize.heroImageLocation}
          alt=""
          layout="fill"
          objectFit="cover"
          sizes="(min-width: 768px) 50vw, 100vw"
        />
        {showLockedPrizeBanner && (
          <div className={styles.lockedBanner}>
            <img src={`${ASSET_PREFIX}/images/prizes/padlock.svg`} alt="" />
            <p className={'aboutUsTypographyCaption ' + styles.lockedText}>
              {props.prize.drawTypePlans?.map(capitalizeFirstLetter).join('/')}{' '}
              Prize
            </p>
          </div>
        )}
        {additionalComponentBadge && (
          <div className={styles.badgeHolder}>{additionalComponentBadge}</div>
        )}
      </AspectRatioContainer>
      <figcaption className={styles.textContainer}>
        <HeadingElement
          className={'aboutUsTypographyHeaderMedium ' + styles.heading}
        >
          <Link
            href={'/prizes/details/' + props.prize.slug}
            className={styles.fullSizeLink}
          >
            {props.prize.title}
          </Link>
        </HeadingElement>
        <p className={styles.subheading}>{props.prize.subheading}</p>

        {props.showPrizeTierBadge && (
          <div className={styles.prizeTierBadgeContainer}>
            <PrizeBadgeOverlay prizeTierCode={props.prize.tierCode} />
          </div>
        )}
      </figcaption>
      <div
        className={`${styles.favouriteButtonContainer} ${props.imageAspectRatio === 'WIDE' ? styles.favouriteButtonContainerAsWide : styles.favouriteButtonContainerAsSquare}`}
      >
        <FavouriteButton
          rebrand2025
          favourited={isFavourited}
          onToggle={handleFavouriteToggle}
        />
      </div>
      {showUnfavouritePrizeModal && (
        <UnfavouritePrizeModal
          onConfirm={async () => {
            await updateFavourite(prizeId, false, fetchWishListPrizes);
            setShowUnfavouritePrizeModal(false);
          }}
          onClose={() => setShowUnfavouritePrizeModal(false)}
          prizeName={props.prize.title}
          prizeHero={props.prize.heroImageLocation}
        />
      )}
    </figure>
  );
});

export default PrizeCard2026;
